const DEFAULT_PASSED_MESSAGE = 'All test steps completed successfully.';
const DEFAULT_FAILED_MESSAGE = 'Test execution failed.';

function pickFirstMessage(...candidates) {
    for (const candidate of candidates) {
        if (typeof candidate === 'string' && candidate.trim()) {
            return candidate.trim();
        }
    }
    return null;
}

/**
 * Normalize testComplete / saveTestReport overall status for execution UI.
 */
export function normalizeOverallStatus(raw, fallbackMessage = null) {
    if (raw == null && !fallbackMessage) return null;

    if (typeof raw === 'string') {
        const normalized = raw.trim().toLowerCase();
        const status =
            normalized === 'passed' || normalized === 'success'
                ? 'passed'
                : normalized === 'failed' || normalized === 'failure'
                  ? 'failed'
                  : null;
        if (!status) return fallbackMessage ? { status: null, message: fallbackMessage } : null;
        return { status, message: fallbackMessage ?? null };
    }

    if (raw != null && typeof raw === 'object') {
        const statusRaw = raw.status ?? raw.overallStatus ?? null;
        const message =
            raw.message ??
            raw.overallMessage ??
            fallbackMessage ??
            null;
        if (!statusRaw && !message) return null;

        const status =
            typeof statusRaw === 'string'
                ? statusRaw.toLowerCase() === 'passed' || statusRaw.toLowerCase() === 'success'
                    ? 'passed'
                    : statusRaw.toLowerCase() === 'failed'
                      ? 'failed'
                      : statusRaw
                : statusRaw;

        return { status, message };
    }

    return fallbackMessage ? { status: null, message: fallbackMessage } : null;
}

/**
 * Resolve pass/fail banner copy from a raw testComplete WebSocket payload.
 * Handles nested overallStatus objects, legacy string enums, and top-level overallMessage.
 */
export function extractTestCompleteOverallStatus(result) {
    if (!result || typeof result !== 'object') return null;

    const nestedOverall =
        result.overallStatus && typeof result.overallStatus === 'object'
            ? result.overallStatus
            : null;

    const fallbackMessage = pickFirstMessage(
        result.overallMessage,
        result.message,
        nestedOverall?.overallMessage,
        nestedOverall?.message,
        result.error
    );

    let normalized = normalizeOverallStatus(result.overallStatus, fallbackMessage);

    if (!normalized?.status && result.success != null) {
        const status = result.success === false ? 'failed' : 'passed';
        return {
            status,
            message:
                normalized?.message ||
                fallbackMessage ||
                (status === 'passed' ? DEFAULT_PASSED_MESSAGE : DEFAULT_FAILED_MESSAGE),
        };
    }

    if (!normalized) return null;

    if (!normalized.message) {
        return {
            ...normalized,
            message:
                fallbackMessage ||
                (normalized.status === 'passed'
                    ? DEFAULT_PASSED_MESSAGE
                    : normalized.status === 'failed'
                      ? DEFAULT_FAILED_MESSAGE
                      : null),
        };
    }

    return normalized;
}
